import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { Check, ShoppingCart, ArrowLeft, ArrowRight, X, ChevronDown } from "lucide-react";
import { PageShell } from "@/components/site/page-shell";
import { supabase } from "@/integrations/supabase/client";
import { fetchProductBySlug, formatPrice, intervalLabel, type StorefrontProduct, type StorefrontVariant } from "@/lib/storefront";
import { cartStore } from "@/lib/cart";
import { toast } from "sonner";

export const Route = createFileRoute("/p/$slug")({
  component: UniversalSlugPage,
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug} — CoderLity` },
      { property: "og:url", content: `/p/${params.slug}` },
    ],
    links: [{ rel: "canonical", href: `https://coderlity.com/p/${params.slug}` }],
  }),
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-32 text-center">
        <h1 className="text-3xl font-bold">Not found</h1>
        <Link to="/" className="mt-6 inline-block text-primary">Back home</Link>
      </div>
    </PageShell>
  ),
  errorComponent: ({ error }) => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-32 text-center">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="mt-2 text-muted-foreground">{error.message}</p>
      </div>
    </PageShell>
  ),
});

type Resolved =
  | { kind: "product"; product: StorefrontProduct }
  | { kind: "page"; page: any }
  | { kind: "none" };

function UniversalSlugPage() {
  const { slug } = Route.useParams();
  const { data, isLoading } = useQuery<Resolved>({
    queryKey: ["universal-slug", slug],
    queryFn: async () => {
      const product = await fetchProductBySlug(slug);
      if (product) return { kind: "product", product };
      const { data: page, error } = await supabase
        .from("pages")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      if (page) return { kind: "page", page };
      return { kind: "none" };
    },
  });

  if (isLoading) {
    return (
      <PageShell>
        <div className="mx-auto max-w-3xl px-4 py-32 text-center text-muted-foreground">Loading…</div>
      </PageShell>
    );
  }
  if (!data || data.kind === "none") throw notFound();
  if (data.kind === "product") return <ProductView product={data.product} />;
  return <CmsView page={data.page} />;
}

function ProductView({ product }: { product: StorefrontProduct }) {
  const [variantId, setVariantId] = useState<string | null>(null);
  const selected: StorefrontVariant | undefined = useMemo(() => {
    if (!product.variants.length) return undefined;
    return product.variants.find((v) => v.id === variantId) || product.variants[0];
  }, [product, variantId]);

  const backHref =
    product.kind === "software" ? "/software" :
    product.kind === "service" ? "/services" :
    product.kind === "hosting" || product.kind === "vps" ? "/hosting" :
    product.kind === "domain" ? "/domains" : "/marketplace";

  function addToCart() {
    if (!selected) return;
    cartStore.add({
      id: selected.id,
      name: `${product.name} — ${selected.name}`,
      price: selected.price_cents / 100,
      qty: 1,
      meta: intervalLabel(selected.billing_interval).trim() || undefined,
      href: `/p/${product.slug}`,
    });
    toast.success("Added to cart");
  }

  return (
    <PageShell>
      <div className="mx-auto max-w-6xl px-4 lg:px-8 py-16">
        <Link to={backHref} className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="grid gap-10 lg:grid-cols-[1.2fr,1fr]">
          <div>
            {product.cover_url && (
              <img src={product.cover_url} alt={product.name} className="w-full rounded-2xl border border-border object-cover" />
            )}
            <div className="mt-4 text-xs font-semibold uppercase tracking-widest text-primary">{product.kind}</div>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">{product.name}</h1>
            {product.description && (
              <p className="mt-4 whitespace-pre-line text-muted-foreground">{product.description}</p>
            )}
            {product.kind === "software" && product.metadata?.screenshot_url && (
              <figure className="mt-6">
                <img
                  src={product.metadata.screenshot_url as string}
                  alt={`${product.name} screenshot preview`}
                  className="w-full rounded-xl border border-border object-cover shadow-sm"
                  loading="lazy"
                />
                <figcaption className="mt-2 text-xs text-muted-foreground">Preview of {product.name}</figcaption>
              </figure>
            )}
            {Array.isArray(product.metadata?.features) && (
              <ul className="mt-6 grid gap-2 sm:grid-cols-2">
                {product.metadata.features.map((f: string) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />{f}
                  </li>
                ))}
              </ul>
            )}
            {product.metadata?.specs && typeof product.metadata.specs === "object" && (
              <div className="mt-8 rounded-xl border border-border bg-card p-5">
                <div className="mb-3 text-sm font-semibold">Specifications</div>
                <dl className="grid gap-2 sm:grid-cols-2 text-sm">
                  {Object.entries(product.metadata.specs as Record<string, any>).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 border-b border-border/50 pb-1">
                      <dt className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</dt>
                      <dd className="font-medium">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
          <aside className="h-fit rounded-2xl border border-border bg-card p-6 shadow-card sticky top-24">
            {selected ? (
              <>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold">{formatPrice(selected.price_cents, selected.currency)}</span>
                  <span className="text-sm text-muted-foreground">{intervalLabel(selected.billing_interval)}</span>
                </div>
                {product.variants.length > 1 && (
                  <div className="mt-5 space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Choose a plan</div>
                    {product.variants.map((v) => {
                      const active = selected.id === v.id;
                      const oos = v.stock_tracked && v.stock_qty <= 0;
                      return (
                        <button
                          key={v.id}
                          disabled={oos}
                          onClick={() => setVariantId(v.id)}
                          className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition ${
                            active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                          } ${oos ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{v.name}</span>
                            <span className="font-semibold">
                              {formatPrice(v.price_cents, v.currency)}
                              <span className="text-xs text-muted-foreground">{intervalLabel(v.billing_interval)}</span>
                            </span>
                          </div>
                          {oos && <div className="mt-1 text-xs text-destructive">Out of stock</div>}
                        </button>
                      );
                    })}
                  </div>
                )}
                <button
                  onClick={addToCart}
                  disabled={selected.stock_tracked && selected.stock_qty <= 0}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-brand px-4 py-3 text-sm font-semibold text-white shadow-glow disabled:opacity-50"
                >
                  <ShoppingCart className="h-4 w-4" /> Add to cart
                </button>
                <Link to="/cart" className="mt-2 block text-center text-xs text-muted-foreground hover:text-foreground">
                  View cart →
                </Link>
              </>
            ) : (
              <Link to="/contact" className="inline-flex w-full items-center justify-center rounded-md bg-gradient-brand px-4 py-3 text-sm font-semibold text-white shadow-glow">
                Contact for pricing
              </Link>
            )}
          </aside>
        </div>
      </div>
    </PageShell>
  );
}

type Block =
  | { type: "hero"; title?: string; subtitle?: string; eyebrow?: string; image?: string; ctaLabel?: string; ctaHref?: string }
  | { type: "heading"; text?: string; level?: 2 | 3 }
  | { type: "text"; markdown?: string }
  | { type: "image"; src?: string; alt?: string; caption?: string }
  | { type: "gallery"; images?: { src: string; alt?: string }[] }
  | { type: "features"; title?: string; items?: { title?: string; description?: string; icon?: string }[] }
  | { type: "faq"; title?: string; items?: { question: string; answer: string }[] }
  | { type: "compare"; title?: string; columns?: string[]; rows?: { label: string; values: (string | boolean)[] }[] }
  | { type: "cta"; title?: string; subtitle?: string; primaryLabel?: string; primaryHref?: string; secondaryLabel?: string; secondaryHref?: string }
  | { type: string; [k: string]: any };

function CmsView({ page }: { page: any }) {
  const blocks: Block[] = Array.isArray(page.blocks) ? page.blocks : [];
  return (
    <PageShell>
      <article className="mx-auto max-w-5xl px-4 lg:px-8 py-16 space-y-16">
        {blocks.length === 0 ? (
          <h1 className="text-4xl font-bold tracking-tight">{page.title}</h1>
        ) : (
          blocks.map((b, i) => <BlockRenderer key={i} block={b} />)
        )}
      </article>
    </PageShell>
  );
}

function isExternal(href?: string) {
  return !!href && /^https?:\/\//i.test(href);
}

function SmartLink({ href, className, children }: { href?: string; className?: string; children: React.ReactNode }) {
  if (!href) return null;
  if (isExternal(href)) {
    return <a href={href} target="_blank" rel="noreferrer" className={className}>{children}</a>;
  }
  return <Link to={href} className={className}>{children}</Link>;
}

function BlockRenderer({ block }: { block: Block }) {
  switch (block.type) {
    case "hero":
      return (
        <section className="grid items-center gap-10 py-8 md:grid-cols-2">
          <div>
            {block.eyebrow && <div className="text-xs font-semibold uppercase tracking-widest text-primary">{block.eyebrow}</div>}
            {block.title && <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">{block.title}</h1>}
            {block.subtitle && <p className="mt-5 text-lg text-muted-foreground">{block.subtitle}</p>}
            {block.ctaLabel && block.ctaHref && (
              <SmartLink
                href={block.ctaHref}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-gradient-brand px-5 py-3 text-sm font-semibold text-white shadow-glow"
              >
                {block.ctaLabel} <ArrowRight className="h-4 w-4" />
              </SmartLink>
            )}
          </div>
          {block.image && (
            <img src={block.image} alt={block.title || ""} className="w-full rounded-2xl border border-border object-cover" loading="lazy" />
          )}
        </section>
      );

    case "heading": {
      const Tag = (block.level === 3 ? "h3" : "h2") as "h2" | "h3";
      return (
        <Tag className={Tag === "h2" ? "text-3xl font-bold tracking-tight" : "text-xl font-semibold"}>
          {block.text}
        </Tag>
      );
    }

    case "text":
      return (
        <div className="prose prose-invert max-w-none prose-a:text-primary prose-headings:tracking-tight">
          <ReactMarkdown>{block.markdown || ""}</ReactMarkdown>
        </div>
      );

    case "image":
      return block.src ? (
        <figure className="space-y-2">
          <img src={block.src} alt={block.alt || ""} className="w-full rounded-2xl border border-border" loading="lazy" />
          {block.caption && <figcaption className="text-center text-xs text-muted-foreground">{block.caption}</figcaption>}
        </figure>
      ) : null;

    case "gallery": {
      const imgs = block.images ?? [];
      if (!imgs.length) return null;
      return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {imgs.map((img: any, i: number) => (
            <img
              key={i}
              src={img.src}
              alt={img.alt || ""}
              className="aspect-[4/3] w-full rounded-xl border border-border object-cover"
              loading="lazy"
            />
          ))}
        </div>
      );
    }

    case "features": {
      const items = block.items ?? [];
      if (!items.length) return null;
      return (
        <section className="space-y-6">
          {block.title && <h2 className="text-3xl font-bold tracking-tight">{block.title}</h2>}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((it: any, i: number) => (
              <div key={i} className="rounded-xl border border-border bg-card p-5">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Check className="h-4 w-4" />
                </div>
                {it.title && <div className="mt-3 font-semibold">{it.title}</div>}
                {it.description && <p className="mt-1 text-sm text-muted-foreground">{it.description}</p>}
              </div>
            ))}
          </div>
        </section>
      );
    }

    case "faq": {
      const items = block.items ?? [];
      if (!items.length) return null;
      return (
        <section className="space-y-5">
          {block.title && <h2 className="text-3xl font-bold tracking-tight">{block.title}</h2>}
          <div className="divide-y divide-border rounded-2xl border border-border bg-card">
            {items.map((q: any, i: number) => (
              <details key={i} className="group p-5 [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-left font-medium">
                  <span>{q.question}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition group-open:rotate-180" />
                </summary>
                <div className="mt-3 text-sm text-muted-foreground whitespace-pre-line">{q.answer}</div>
              </details>
            ))}
          </div>
        </section>
      );
    }

    case "compare": {
      const cols = block.columns ?? [];
      const rows = block.rows ?? [];
      if (!cols.length || !rows.length) return null;
      return (
        <section className="space-y-5">
          {block.title && <h2 className="text-3xl font-bold tracking-tight">{block.title}</h2>}
          <div className="overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-medium"></th>
                  {cols.map((c: string) => (
                    <th key={c} className="px-4 py-3 text-left font-medium">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r: any, i: number) => (
                  <tr key={i}>
                    <th scope="row" className="px-4 py-3 text-left font-medium">{r.label}</th>
                    {cols.map((_: string, ci: number) => {
                      const v = r.values?.[ci];
                      return (
                        <td key={ci} className="px-4 py-3 align-top">
                          {typeof v === "boolean" ? (
                            v ? <Check className="h-4 w-4 text-primary" /> : <X className="h-4 w-4 text-muted-foreground/50" />
                          ) : (
                            <span className="text-foreground/90">{v ?? "—"}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );
    }

    case "cta":
      return (
        <section className="rounded-2xl border border-border bg-gradient-brand/10 p-8 text-center sm:p-12">
          {block.title && <h2 className="text-3xl font-bold tracking-tight">{block.title}</h2>}
          {block.subtitle && <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{block.subtitle}</p>}
          {(block.primaryLabel || block.secondaryLabel) && (
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {block.primaryLabel && block.primaryHref && (
                <SmartLink
                  href={block.primaryHref}
                  className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-5 py-3 text-sm font-semibold text-white shadow-glow"
                >
                  {block.primaryLabel} <ArrowRight className="h-4 w-4" />
                </SmartLink>
              )}
              {block.secondaryLabel && block.secondaryHref && (
                <SmartLink
                  href={block.secondaryHref}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold"
                >
                  {block.secondaryLabel}
                </SmartLink>
              )}
            </div>
          )}
        </section>
      );

    default:
      return null;
  }
}
